import { AnimatePresence, motion } from "framer-motion";
import { Link } from "react-router-dom";
import { X, Minus, Plus, Trash2 } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { useCart } from "@/context/CartContext";

const CartDrawer = () => {
  const { t, tr } = useLang();
  const { items, open, setOpen, remove, setQty, subtotal, count } = useCart();
  const shippingFree = subtotal >= 50 || subtotal === 0;

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="fixed inset-0 bg-ink/40 z-[60]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setOpen(false)}
            data-testid="cart-overlay"
          />
          <motion.aside
            className="fixed top-0 right-0 h-full w-full sm:w-[440px] bg-cream z-[70] border-l border-ink flex flex-col"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 300 }}
            data-testid="cart-drawer"
          >
            <div className="flex items-center justify-between p-5 border-b border-ink bg-lime">
              <h2 className="display uppercase text-2xl">{t.cart.title} ({count})</h2>
              <button onClick={() => setOpen(false)} data-testid="cart-close" className="btn bg-cream w-9 h-9 p-0 hover:bg-ink hover:text-cream">
                <X className="w-4 h-4" strokeWidth={2} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-5">
              {items.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center gap-5">
                  <p className="display uppercase text-3xl text-ink/40">{t.cart.empty}</p>
                  <Link to="/shop" onClick={() => setOpen(false)} className="btn-primary h-11 px-6">
                    {t.cart.emptyCta}
                  </Link>
                </div>
              ) : (
                <ul className="space-y-3">
                  {items.map((i) => (
                    <li key={i.id} className="flex gap-4 card-line p-3" data-testid={`cart-item-${i.id}`}>
                      <div className="w-20 h-20 bg-sand border border-ink flex-shrink-0 overflow-hidden" data-testid={`cart-thumb-${i.id}`}>
                        {i.images && i.images.length > 1 ? (
                          <div className="grid grid-cols-2 grid-rows-2 w-full h-full">
                            {i.images.slice(0, 4).map((src, idx) => (
                              <img key={idx} src={src} alt="" className="w-full h-full object-cover" />
                            ))}
                          </div>
                        ) : (
                          <img src={i.image} alt={tr(i.name)} className="w-full h-full object-cover" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-bold leading-tight">{tr(i.name)}</p>
                        <p className="font-bold text-pink">€{i.price}</p>
                        <div className="flex items-center gap-2 mt-2">
                          <button onClick={() => setQty(i.id, i.qty - 1)} data-testid={`cart-dec-${i.id}`} className="w-7 h-7 border border-ink rounded-full flex items-center justify-center bg-white hover:bg-lime transition-colors" aria-label="minus">
                            <Minus className="w-3 h-3" strokeWidth={2.5} />
                          </button>
                          <span className="w-8 text-center font-bold" data-testid={`cart-qty-${i.id}`}>{i.qty}</span>
                          <button onClick={() => setQty(i.id, i.qty + 1)} data-testid={`cart-inc-${i.id}`} className="w-7 h-7 border border-ink rounded-full flex items-center justify-center bg-white hover:bg-lime transition-colors" aria-label="plus">
                            <Plus className="w-3 h-3" strokeWidth={2.5} />
                          </button>
                          <button onClick={() => remove(i.id)} data-testid={`cart-remove-${i.id}`} className="ml-auto text-ink/40 hover:text-pink transition-colors" aria-label={t.cart.remove}>
                            <Trash2 className="w-4 h-4" strokeWidth={2} />
                          </button>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {items.length > 0 && (
              <div className="p-5 border-t border-ink bg-white space-y-3">
                <div className="flex justify-between font-medium">
                  <span>{t.cart.shipping}</span>
                  <span className={shippingFree ? "text-pink font-bold uppercase" : ""}>{shippingFree ? t.cart.free : "€4,90"}</span>
                </div>
                <div className="flex justify-between items-end">
                  <span className="display uppercase text-xl">{t.cart.subtotal}</span>
                  <span className="display text-3xl" data-testid="cart-subtotal">€{subtotal.toFixed(2)}</span>
                </div>
                <Link
                  to="/checkout"
                  onClick={() => setOpen(false)}
                  data-testid="cart-checkout"
                  className="btn-primary w-full h-14 text-lg"
                >
                  {t.cart.checkout}
                </Link>
                <p className="text-xs text-center text-ink/50">{t.cart.demoNote}</p>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};

export default CartDrawer;
